// ============================================================================
// BESPOKIAN Health - Pricing Page
// Transparent pricing with luxury aesthetic
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Calculator, TrendingDown, Shield, DollarSign, ArrowRight, MessageCircle } from 'lucide-react';

const pricingFeatures = [
  { icon: DollarSign, text: 'Actual drug acquisition cost' },
  { icon: TrendingDown, text: '15% markup (capped at $300)' },
  { icon: Calculator, text: '$5 flat dispensing fee' },
  { icon: Shield, text: 'No hidden fees, ever' },
];

const comparisonDrugs = [
  { name: 'Lisinopril 10mg', quantity: '30 tablets', retail: 18.00, ours: 7.76 },
  { name: 'Metformin 500mg', quantity: '60 tablets', retail: 22.00, ours: 8.45 },
  { name: 'Atorvastatin 20mg', quantity: '30 tablets', retail: 35.00, ours: 12.85 },
  { name: 'Omeprazole 20mg', quantity: '30 capsules', retail: 28.00, ours: 9.95 },
  { name: 'Sertraline 50mg', quantity: '30 tablets', retail: 32.00, ours: 11.20 },
];

export function PricingPage() {
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-content');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1">
        {/* Hero Section with Chemistry Lab Background */}
        <section
          className="relative h-screen flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 21, 58, 0.55), rgba(0, 21, 58, 0.9)), url("/img/heroes/health-pricing-hero.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center center'
          }}
        >
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-8" />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight tracking-tight">
              Transparent Pharmacy Pricing
            </h1>
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-12" />

            {/* Learn More - Scroll to pricing section */}
            <div className="max-w-xl mx-auto">
              <div
                id="learn-more"
                role="button"
                tabIndex={0}
                onClick={scrollToPricing}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') scrollToPricing();
                }}
                className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 border border-white/20 shadow-xl ring-1 ring-white/10 cursor-pointer hover:bg-white/20 transition-all duration-300 w-full"
                aria-label="Learn more about pricing"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="text-left">
                    <div className="text-white/90 text-xs uppercase tracking-widest mb-2">
                      LEARN MORE
                    </div>
                    <div className="text-white/70 text-lg">
                      See how it works
                    </div>
                  </div>
                  <MessageCircle className="h-6 w-6 text-white/80 flex-shrink-0" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Content Section */}
        <div id="pricing-content">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Gold accent line */}
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Transparent Pharmacy Pricing
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              See Exactly What You Pay
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              No PBM middlemen. No hidden fees. Just the actual drug cost + a simple, honest markup.
            </p>
          </div>
        </section>

        {/* Pricing Formula Section */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Formula Card */}
              <div className="bg-navy-800 rounded-2xl p-8 text-white">
                <h2 className="font-serif text-2xl italic mb-6">The BESPOKIAN Formula</h2>

                {/* Visual Formula */}
                <div className="bg-navy-700/50 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-center gap-3 text-xl font-mono">
                    <span className="text-white">Drug Cost</span>
                    <span className="text-gold-400">+</span>
                    <span className="text-gold-400 font-bold">15%</span>
                    <span className="text-gold-400">+</span>
                    <span className="text-gold-400 font-bold">$5</span>
                    <span className="text-white">=</span>
                    <span className="text-sage-400 font-bold">Your Price</span>
                  </div>
                </div>

                <p className="text-navy-200 text-sm">
                  That's it. We buy medications at wholesale prices, add a transparent 15% margin
                  (capped at $300 for expensive drugs), and charge a flat $5 dispensing fee.
                </p>
              </div>

              {/* Features List */}
              <div>
                <h3 className="font-display text-xl text-navy-800 tracking-tight mb-6">What This Means For You</h3>
                <div className="space-y-4">
                  {pricingFeatures.map((feature) => (
                    <div key={feature.text} className="flex items-center gap-4 p-4 bg-sage-50 rounded-xl">
                      <div className="w-10 h-10 bg-sage-100 rounded-lg flex items-center justify-center">
                        <feature.icon className="h-5 w-5 text-sage-600" />
                      </div>
                      <span className="text-navy-700 font-medium">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Live Example Section */}
        <section className="py-20 bg-navy-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">
                See It In Action
              </h2>
              <p className="text-navy-600">
                Here's exactly how we calculate the price for a common medication.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-card p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-xl text-navy-800 tracking-tight">Lisinopril 10mg</h3>
                <span className="px-3 py-1 bg-sage-100 text-sage-700 text-sm font-medium rounded-full">
                  30 tablets
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-navy-100">
                  <span className="text-navy-600">Drug Acquisition Cost</span>
                  <span className="font-semibold text-navy-800">$2.40</span>
                </div>
                <div className="flex justify-between py-3 border-b border-navy-100">
                  <span className="text-navy-600">15% Markup</span>
                  <span className="font-semibold text-navy-800">$0.36</span>
                </div>
                <div className="flex justify-between py-3 border-b border-navy-100">
                  <span className="text-navy-600">Dispensing Fee</span>
                  <span className="font-semibold text-navy-800">$5.00</span>
                </div>
                <div className="flex justify-between py-4 bg-gold-50 -mx-4 px-4 rounded-xl mt-4">
                  <span className="font-bold text-navy-800">Your BESPOKIAN Price</span>
                  <span className="text-2xl font-bold text-navy-800">$7.76</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-sage-50 rounded-xl border border-sage-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-sage-600" />
                    <span className="text-sage-700 font-medium">Typical retail price: $18.00</span>
                  </div>
                  <span className="text-sage-700 font-bold">You save 57%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">
                Popular Medications
              </h2>
              <p className="text-navy-600">
                Compare our prices to typical retail pharmacy costs.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-navy-100 overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-4 gap-4 px-6 py-4 bg-navy-50 border-b border-navy-100">
                <div className="col-span-2 text-navy-500 font-medium text-sm uppercase tracking-wider">Medication</div>
                <div className="text-navy-500 font-medium text-sm uppercase tracking-wider text-right">Retail</div>
                <div className="text-navy-500 font-medium text-sm uppercase tracking-wider text-right">BESPOKIAN</div>
              </div>

              {/* Rows */}
              {comparisonDrugs.map((drug, index) => (
                <div
                  key={drug.name}
                  className={`grid grid-cols-4 gap-4 px-6 py-4 ${
                    index !== comparisonDrugs.length - 1 ? 'border-b border-navy-100' : ''
                  } hover:bg-navy-50/50 transition-colors`}
                >
                  <div className="col-span-2">
                    <p className="font-medium text-navy-800">{drug.name}</p>
                    <p className="text-sm text-navy-500">{drug.quantity}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-navy-400 line-through">${drug.retail.toFixed(2)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-navy-800">${drug.ours.toFixed(2)}</span>
                    <p className="text-xs text-sage-600 font-medium">
                      Save {Math.round((1 - drug.ours / drug.retail) * 100)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
            <h2 className="font-display text-3xl text-white tracking-tight italic mb-6">
              Ready to Start Saving?
            </h2>
            <p className="text-xl text-navy-200 mb-8">
              Transfer your prescription or have your doctor send it to us. It takes less than 5 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login">
                <button className="px-8 py-4 text-lg font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg shadow-lg transition-all hover:shadow-glow-gold inline-flex items-center justify-center gap-2">
                  Get Started <ArrowRight className="h-5 w-5" />
                </button>
              </Link>
              <Link to="/login">
                <button className="px-8 py-4 text-lg font-medium text-white border border-navy-600 hover:bg-navy-700 rounded-lg transition-colors">
                  Transfer Prescription
                </button>
              </Link>
            </div>
          </div>
        </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

